import React, { useEffect, useMemo, useState } from "react";
import { X as XIcon, ChevronRight, Sparkles, RefreshCw, Copy } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

// Multi-step modal for Promo Pack V2
// Steps: Platform → Status → Price → Format → Mood

const PLATFORMS = [
  { id: "instagram", label: "Instagram", emoji: "📷", safe: true },
  { id: "tiktok", label: "TikTok", emoji: "🎵", safe: true },
  { id: "bluesky", label: "Bluesky", emoji: "☁️", safe: true },
  { id: "twitter", label: "X (Twitter)", emoji: "🐦", safe: false },
  { id: "onlyfans", label: "OnlyFans", emoji: "💙", safe: false },
  { id: "justforfans", label: "JustForFans", emoji: "🔥", safe: false },
  { id: "jff_inner_circle", label: "JFF Inner Circle", emoji: "💎", safe: false, special: true },
  { id: "rfc", label: "RFC", emoji: "🍑", safe: false },
  { id: "faphouse", label: "Faphouse", emoji: "🎬", safe: false },
  { id: "free_members", label: "Free Members", emoji: "🆓", safe: false },
];

const STATUSES = [
  { id: "free", label: "Free to watch", description: "No paywall" },
  { id: "ppv", label: "Pay-per-view", description: "One-time unlock" },
  { id: "members", label: "Members only", description: "Subscription required" },
  { id: "vip", label: "VIP exclusive", description: "Premium tier only" },
];

const FORMATS = [
  { id: "short", label: "Short", description: "≤80 chars, hook-first" },
  { id: "medium", label: "Medium", description: "150-260 chars, story arc" },
  { id: "long", label: "Long + CTA", description: "350-500 chars, full pitch" },
  { id: "all", label: "All 3 formats", description: "Generate short, medium, and long" },
];

const MOODS = [
  { id: "auto", label: "Auto", emoji: "🎯", description: "AI picks best fit" },
  { id: "alpha", label: "Alpha", emoji: "💪", description: "Confident, dominant ownership" },
  { id: "filthy", label: "Filthy", emoji: "😈", description: "Raw, explicit, gutter-mouthed" },
  { id: "playful", label: "Playful", emoji: "😏", description: "Cheeky, teasing, emoji-friendly" },
  { id: "seductive", label: "Seductive", emoji: "🔥", description: "Slow-burn, sensual, breathy" },
  { id: "provocative", label: "Provocative", emoji: "⚡", description: "Shock-value, stop-the-scroll" },
  { id: "story", label: "Story", emoji: "📖", description: "Narrative, cinematic, scene-setter" },
  { id: "romantic", label: "Romantic", emoji: "❤️", description: "Intimate, warm, connection-focused" },
  { id: "bts", label: "BTS", emoji: "🎬", description: "Behind-the-scenes, insider tone" },
];

// Normalize a platform's caption payload into a display-friendly list.
// Backend returns { short: {text, hashtags}, medium: {...}, long: {...} } where
// each value is an object (not a string). Values may also be plain strings.
const _FORMAT_ORDER = { short: 0, medium: 1, long: 2 };
const CAPTION_PLATFORM = { twitter: "x_twitter", onlyfans: "onlyfans_vip", justforfans: "justforfans", jff_inner_circle: "justforfans", rfc: "rawfuckclub" };
const INCLUDE_OPTIONS = [
  ["title", "Title"], ["free_description", "Free Members Description"], ["promo_note", "Promo-note-derived caption"],
  ["model_mentions", "Model mentions"], ["model_handles", "Model @handles"], ["crew_credits", "Crew credits"],
  ["tags", "Tags / hashtags"], ["cta_text", "CTA headline and supporting text"], ["cta_links", "Individual CTA / purchase links"],
  ["tier_cta", "Tier CTA"], ["linked_photos", "Linked photos"],
];
const DEFAULT_INCLUDE = Object.fromEntries(INCLUDE_OPTIONS.map(([id]) => [id, true]));
const AI_RATE_LIMIT_MESSAGE = "AI provider rate limit reached. Please wait and press Regenerate.";
const AI_FAILURE_MESSAGE = "AI copy generation failed. Please press Regenerate.";
const PROMO_AI_FAILURE_STATUS = "Promo pack created, but AI copy failed. Please press Regenerate.";

function normalizeCaptionEntries(platformData) {
  if (!platformData) return [];
  if (typeof platformData === "string") {
    const text = platformData.trim();
    return text ? [{ label: "caption", text, hashtags: [], copyText: text }] : [];
  }
  if (typeof platformData !== "object") return [];
  const entries = [];
  for (const [label, val] of Object.entries(platformData)) {
    let text = "";
    let hashtags = [];
    if (typeof val === "string") {
      text = val;
    } else if (val && typeof val === "object") {
      text = val.text || val.caption || "";
      hashtags = Array.isArray(val.hashtags) ? val.hashtags : [];
    }
    text = (text || "").trim();
    if (!text) continue;
    const copyText = hashtags.length ? `${text}\n\n${hashtags.join(" ")}` : text;
    entries.push({ label, text, hashtags, copyText });
  }
  entries.sort((a, b) => (_FORMAT_ORDER[a.label] ?? 99) - (_FORMAT_ORDER[b.label] ?? 99));
  return entries;
}

// Rebuild a platform value after inline edits, keeping the original shape and hashtags.
function buildEditedValue(original, drafts) {
  if (typeof original === "string" || original == null) {
    return drafts.caption ?? "";
  }
  const next = {};
  for (const [label, val] of Object.entries(original)) {
    const text = drafts[label] ?? (typeof val === "string" ? val : (val?.text ?? ""));
    next[label] = val && typeof val === "object" ? { ...val, text } : { text, hashtags: [] };
  }
  return next;
}

export default function PromoPackV2Modal({ show, onClose, video }) {
  const [centralVideo, setCentralVideo] = useState(video || null);
  const [metadataDraft, setMetadataDraft] = useState({});
  const [useVideoMetadata, setUseVideoMetadata] = useState(true);
  const [include, setInclude] = useState(DEFAULT_INCLUDE);
  const [selectedCtaLinks, setSelectedCtaLinks] = useState({});
  const [promoImageText, setPromoImageText] = useState("");
  const [paidTiersEnabled, setPaidTiersEnabled] = useState(false);
  const [step, setStep] = useState(1); // 1=Platform, 2=Status, 3=Price, 4=Format, 5=Mood
  const [selections, setSelections] = useState({
    platforms: [],
    status: "free",
    price: 0,
    format: "all",
    mood: "auto",
  });
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [regenerating, setRegenerating] = useState({});
  const [editing, setEditing] = useState(null); // { platform, original, drafts }
  const [savingEdit, setSavingEdit] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(false);

  useEffect(() => {
    if (!show || !video?.id) return;
    let active = true;
    Promise.all([
      api.get(`/api/video-central/${video.id}`),
      api.get("/api/config/features").catch(() => ({ data: { ENABLE_PAID_TIERS: false } })),
    ]).then(([record, featureResponse]) => {
      if (!active) return;
      const data = record.data;
      setCentralVideo(data);
      setPaidTiersEnabled(Boolean(featureResponse.data?.ENABLE_PAID_TIERS));
      const featured = (data.models || []).find((m) => m.featured) || {};
      const cta = data.cta_effective || data.cta || {};
      setMetadataDraft({
        title: data.title || "",
        free_description: data.free_description || "",
        promo_note: data.promo_note || "",
        tags: (data.tags || []).join(", "),
        cast_names: (data.models || []).map((m) => m.name).join(", ") || data.cast || "",
        featured_model: featured.name || "",
        featured_handle: featured.handle || "",
        model_handles: (data.models || []).filter((m) => m.handle).map((m) => `@${m.handle.replace(/^@/, "")}`).join(" "),
        crew_credits: "",
        cta,
        linked_photos: (data.linked_photos || []).map((p) => p.url),
      });
      setSelectedCtaLinks(Object.fromEntries((cta.buttons || []).map((b) => [b.id, Boolean(b.visible)])));
    }).catch(() => toast.error("Could not load central video metadata; using Catalog values"));
    return () => { active = false; };
  }, [show, video?.id, video?.title, video?.tags, video?.cast]);

  const tierCta = useMemo(() => {
    if (!paidTiersEnabled) return "Watch the free preview";
    if (selections.status === "vip") return "Watch the full video in VIP";
    if (selections.status === "members") return "Unlock the full video with Basic";
    return "Watch the free preview";
  }, [paidTiersEnabled, selections.status]);
  const aiCopyFailed = Boolean(result?._ai_failed || result?._missing_platforms?.length);

  if (!show) return null;

  const handleNext = () => {
    // Validate current step
    if (step === 1 && selections.platforms.length === 0) {
      toast.error("Please select at least one platform");
      return;
    }
    
    // Skip price step if status is not PPV
    if (step === 2 && selections.status !== "ppv") {
      setStep(4); // Skip to format
    } else if (step < 5) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step === 4 && selections.status !== "ppv") {
      setStep(2); // Skip back over price
    } else if (step > 1) {
      setStep(step - 1);
    }
  };

  const togglePlatform = (platformId) => {
    const current = selections.platforms;
    if (current.includes(platformId)) {
      setSelections({ ...selections, platforms: current.filter(p => p !== platformId) });
    } else {
      setSelections({ ...selections, platforms: [...current, platformId] });
    }
  };

  const handleMarkAsUsed = async (platform, captionText) => {
    if (!video?.id || !captionText?.trim()) return;
    const matrixPlatform = CAPTION_PLATFORM[platform] || platform;

    try {
      const response = await api.post(`/api/video-central/${video.id}/captions/${matrixPlatform}`, {
        caption: captionText.trim(),
        action: "used",
      });
      const saved = response.data || {};

      setCentralVideo((prev) => ({
        ...(prev || {}),
        platform_captions: {
          ...((prev || {}).platform_captions || {}),
          [matrixPlatform]: saved,
        },
      }));

      setResult((prev) => {
        if (!prev || !prev.captions) return prev;
        return {
          ...prev,
          captions: {
            ...prev.captions,
            [platform]: prev.captions[platform],
          },
        };
      });

      toast.success(`${PLATFORMS.find((p) => p.id === platform)?.label || platform} caption marked as used`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save caption to the Platform Matrix");
    }
  };

  const handleRegenerate = async (platform) => {
    if (!jobId) return;
    setRegenerating((s) => ({ ...s, [platform]: true }));
    try {
      const res = await api.post(`/api/promo/pack/${jobId}/regenerate`, {
        platform,
        format: selections.format,
        mood: selections.mood,
        promo_image_text: promoImageText,
      });
      const { value, generation } = res.data;
      setResult((prev) => {
        if (!prev) return prev;
        const missing = (prev._missing_platforms || []).filter((p) => p !== platform);
        return {
          ...prev,
          [platform]: value,
          _generation: { ...(prev._generation || {}), [platform]: generation },
          _missing_platforms: missing,
          _ai_failed: missing.length > 0,
          _ai_error: missing.length > 0 ? prev._ai_error : undefined,
          _batch_errors: missing.length > 0 ? prev._batch_errors : {},
        };
      });
      toast.success(`${PLATFORMS.find((p) => p.id === platform)?.label || platform} regenerated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (e?.response?.status === 429 ? AI_RATE_LIMIT_MESSAGE : AI_FAILURE_MESSAGE));
    } finally {
      setRegenerating((s) => ({ ...s, [platform]: false }));
    }
  };

  const startEdit = (platform, value) => {
    const drafts = Object.fromEntries(normalizeCaptionEntries(value).map((e) => [e.label, e.text]));
    setEditing({ platform, original: value, drafts });
  };

  const handleSaveEdit = async () => {
    if (!jobId || !editing) return;
    const value = buildEditedValue(editing.original, editing.drafts);
    setSavingEdit(true);
    try {
      await api.put(`/api/promo/pack/${jobId}/platform/${editing.platform}`, { value });
      setResult((prev) => (prev ? { ...prev, [editing.platform]: value } : prev));
      setEditing(null);
      toast.success("Changes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleLoadLatest = async () => {
    if (!video?.id) return;
    setLoadingLatest(true);
    try {
      const res = await api.get(`/api/promo/video/${video.id}/latest`);
      const job = res.data || {};
      setJobId(job.job_id);
      setResult(job.result || null);
      if (Array.isArray(job.platforms) && job.platforms.length) {
        setSelections((s) => ({ ...s, platforms: job.platforms.filter((p) => PLATFORMS.some((x) => x.id === p)) }));
      }
      toast.success("Loaded latest saved promo pack");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No saved promo pack for this video yet" : "Could not load the saved promo pack");
    } finally {
      setLoadingLatest(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setProgress(0);
    
    try {
      // Start generation
      const startRes = await api.post("/api/promo/pack", {
        video_id: video.id,
        mood: selections.mood,
        variation_seed: crypto?.randomUUID?.() || String(Date.now()),
        // Additional metadata for V2
        platforms: selections.platforms,
        status: selections.status,
        price: selections.status === "ppv" ? selections.price : 0,
        format: selections.format,
        promo_image_text: promoImageText,
        overrides: {
          platforms: selections.platforms,
          include: useVideoMetadata ? include : Object.fromEntries(Object.keys(DEFAULT_INCLUDE).map((key) => [key, false])),
          title: metadataDraft.title,
          free_description: metadataDraft.free_description,
          promo_note: metadataDraft.promo_note,
          tags: (metadataDraft.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
          cast_names: (metadataDraft.cast_names || "").split(",").map((n) => n.trim()).filter(Boolean),
          featured_model: metadataDraft.featured_model,
          featured_handle: metadataDraft.featured_handle,
          model_handles: metadataDraft.model_handles,
          crew_credits: metadataDraft.crew_credits,
          cta: metadataDraft.cta,
          cta_link_labels: (metadataDraft.cta?.buttons || []).filter((b) => selectedCtaLinks[b.id] && b.visible).map((b) => `${b.label}: ${b.url}`),
          tier_cta: tierCta,
          linked_photos: metadataDraft.linked_photos || [],
          member_tier: centralVideo?.tiers?.[0] || "free",
        },
      });
      
      const jobId = startRes.data.job_id;
      let timeoutReached = false;
      let consecutiveErrors = 0; // tolerate transient 5xx from the status poll
      
      // Timeout after 10 minutes (increased from 5)
      const timeoutId = setTimeout(() => {
        timeoutReached = true;
        toast.error('⏱️ Generation timed out. This may happen with longer videos or slow AI responses. Please try again or contact support if the issue persists.');
        setGenerating(false);
      }, 600000); // 10 minutes
      
      // Poll for results every 3 seconds
      const pollInterval = setInterval(async () => {
        if (timeoutReached) {
          clearInterval(pollInterval);
          return;
        }
        
        try {
          const statusRes = await api.get(`/api/promo/status/${jobId}`);
          consecutiveErrors = 0; // reset on any successful poll
          const { status, progress: jobProgress, result: jobResult, error } = statusRes.data;
          
          setProgress(jobProgress || 0);
          
          if (status === 'complete') {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            setResult(jobResult);
            setJobId(jobId);
            setGenerating(false);
            if (jobResult?._ai_failed || jobResult?._missing_platforms?.length) {
              toast.error(jobResult?._ai_error || AI_FAILURE_MESSAGE);
            } else {
              toast.success('✅ Promo pack generated successfully!');
            }
          } else if (status === 'error') {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            toast.error(`❌ Generation failed: ${error || 'Unknown error'}. Please try again.`);
            setGenerating(false);
          } else if (status === 'processing') {
            // Still processing, show encouraging message periodically
            if (jobProgress && jobProgress % 25 === 0 && jobProgress > 0) {
              toast.info(`🎬 ${jobProgress}% complete - AI is working on your promo...`, { duration: 2000 });
            }
          }
        } catch (pollErr) {
          // Transient errors (502/503 from ingress, brief network blips) are common
          // during long generations — keep polling and only give up after several
          // consecutive failures so a single blip doesn't strand a good job.
          consecutiveErrors += 1;
          console.error(`Polling error (${consecutiveErrors}/5):`, pollErr);
          if (consecutiveErrors >= 5) {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            toast.error('❌ Lost connection while checking generation status. Please try again.');
            setGenerating(false);
          }
        }
      }, 3000);
      
    } catch (err) {
      console.error('Generation error:', err);
      toast.error(err?.response?.status === 429 ? AI_RATE_LIMIT_MESSAGE : AI_FAILURE_MESSAGE);
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelections({
      platforms: [],
      status: "free",
      price: 0,
      format: "all",
      mood: "auto",
    });
    setGenerating(false);
    setProgress(0);
    setResult(null);
    setJobId(null);
    setEditing(null);
    setInclude(DEFAULT_INCLUDE);
    setUseVideoMetadata(true);
    setSelectedCtaLinks({});
    setPromoImageText("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-black/95 backdrop-blur-xl border border-white/10">
        
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-white/10 bg-black/95 backdrop-blur-xl">
          <div>
            <div className="text-xs font-bold uppercase mb-1 text-purple-400 flex items-center gap-2">
              <Sparkles size={14} /> PROMO PACK V2
            </div>
            <h2 className="text-xl font-bold text-white">{centralVideo?.title || video.title}</h2>
          </div>
          <button onClick={handleClose} className="p-2 rounded hover:bg-zinc-800/50 text-zinc-400 hover:text-white transition">
            <XIcon size={24} />
          </button>
        </div>

        {!generating && !result && (
          <section className="mx-6 mt-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="promo-video-metadata">
            <label className="flex items-center gap-2 text-sm font-semibold text-white">
              <input type="checkbox" checked={useVideoMetadata} onChange={(e) => setUseVideoMetadata(e.target.checked)} className="accent-purple-500" />
              Use video metadata from Member Areas / Catalog
            </label>
            {useVideoMetadata && (
              <div className="mt-4 space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  {[["title", "Title"], ["featured_model", "Featured model"], ["featured_handle", "Featured @handle"], ["cast_names", "Models / cast (comma separated)"], ["model_handles", "Model @handles"], ["crew_credits", "Crew credits"]].map(([key, label]) => (
                    <label key={key} className="block text-xs text-zinc-400">{label}
                      <input value={metadataDraft[key] || ""} onChange={(e) => setMetadataDraft((s) => ({ ...s, [key]: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
                    </label>
                  ))}
                  <label className="block text-xs text-zinc-400">Tags / hashtags (comma separated)
                    <input value={metadataDraft.tags || ""} onChange={(e) => setMetadataDraft((s) => ({ ...s, tags: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
                  </label>
                </div>
                <label className="block text-xs text-zinc-400">Free Members Description
                  <textarea rows={2} value={metadataDraft.free_description || ""} onChange={(e) => setMetadataDraft((s) => ({ ...s, free_description: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
                </label>
                <label className="block text-xs text-zinc-400">Promo Note
                  <textarea rows={2} value={metadataDraft.promo_note || ""} onChange={(e) => setMetadataDraft((s) => ({ ...s, promo_note: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-xs text-zinc-400">CTA headline
                    <input value={metadataDraft.cta?.headline || ""} onChange={(e) => setMetadataDraft((s) => ({ ...s, cta: { ...(s.cta || {}), headline: e.target.value } }))}
                      className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
                  </label>
                  <label className="block text-xs text-zinc-400">CTA supporting text
                    <input value={metadataDraft.cta?.text || ""} onChange={(e) => setMetadataDraft((s) => ({ ...s, cta: { ...(s.cta || {}), text: e.target.value } }))}
                      className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 border-t border-zinc-800 pt-3">
                  {INCLUDE_OPTIONS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-xs text-zinc-300">
                      <input type="checkbox" checked={Boolean(include[key])} onChange={(e) => setInclude((s) => ({ ...s, [key]: e.target.checked }))} className="accent-purple-500" />{label}
                    </label>
                  ))}
                </div>
                {include.cta_links && (metadataDraft.cta?.buttons || []).length > 0 && (
                  <div className="border-t border-zinc-800 pt-3 space-y-2">
                    <div className="text-xs font-semibold text-zinc-400">Individual CTA / purchase links</div>
                    {(metadataDraft.cta.buttons || []).map((button) => (
                      <label key={button.id} className="flex items-center gap-2 text-xs text-zinc-300">
                        <input type="checkbox" checked={Boolean(selectedCtaLinks[button.id])} onChange={(e) => setSelectedCtaLinks((s) => ({ ...s, [button.id]: e.target.checked }))} className="accent-purple-500" />
                        {button.label}<span className="text-zinc-600 truncate">{button.url}</span>
                      </label>
                    ))}
                  </div>
                )}
                {include.linked_photos && <p className="text-xs text-zinc-500">{(metadataDraft.linked_photos || []).length} linked photo asset(s) will be attached to this promo job.</p>}
              </div>
            )}
          </section>
        )}

        {/* Progress Stepper */}
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: "Platform" },
              { num: 2, label: "Status" },
              ...(selections.status === "ppv" ? [{ num: 3, label: "Price" }] : []),
              { num: 4, label: "Format" },
              { num: 5, label: "Mood" },
            ].map((s, idx, arr) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition ${
                    step === s.num 
                      ? "bg-purple-600 text-white" 
                      : step > s.num 
                        ? "bg-purple-500/30 text-purple-300"
                        : "bg-zinc-800 text-zinc-500"
                  }`}>
                    {s.num}
                  </div>
                  <div className={`text-xs mt-1 ${step === s.num ? "text-white" : "text-zinc-500"}`}>
                    {s.label}
                  </div>
                </div>
                {idx < arr.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.num ? "bg-purple-500/30" : "bg-zinc-800"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          {/* Step 1: Platform Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Select Platforms</h3>
                <p className="text-sm text-zinc-400 mb-4">Choose which platforms to generate captions for</p>
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleLoadLatest}
                    disabled={loadingLatest || !video?.id}
                    className="text-xs px-3 py-2 border border-white/15 hover:border-purple-500 text-zinc-300 hover:text-white rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                    data-testid="promo-reuse-latest">
                    <RefreshCw size={12} className={loadingLatest ? "animate-spin" : ""} /> Reuse latest saved pack
                  </button>
                  {selections.platforms.includes("free_members") && !(metadataDraft.free_description || "").trim() && (
                    <span className="text-xs text-amber-300">Add a Free Members Description in the metadata section for better Free Members copy.</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={`p-4 rounded-lg border-2 text-left transition ${
                      selections.platforms.includes(platform.id)
                        ? "border-purple-500 bg-purple-500/10"
                        : "border-white/10 hover:border-white/20 bg-black/40"
                    }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{platform.emoji}</span>
                      <span className="font-semibold text-white">{platform.label}</span>
                    </div>
                    <div className="text-xs text-zinc-500">
                      {platform.safe ? "Safe content" : "Explicit allowed"}
                    </div>
                  </button>
                ))}
              </div>
              {selections.platforms.length > 0 && (
                <div className="text-sm text-purple-400 mt-2">
                  {selections.platforms.length} platform{selections.platforms.length !== 1 ? "s" : ""} selected
                </div>
              )}
            </div>
          )}

          {/* Step 2: Status Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Video Status</h3>
                <p className="text-sm text-zinc-400 mb-4">How is this video monetized?</p>
              </div>
              <div className="space-y-2">
                {STATUSES.filter((status) => paidTiersEnabled || !["members", "vip"].includes(status.id)).map((status) => (
                  <button
                    key={status.id}
                    onClick={() => setSelections({ ...selections, status: status.id })}
                    className={`w-full p-4 rounded-lg border-2 text-left transition ${
                      selections.status === status.id
                        ? "border-purple-500 bg-purple-500/10"
                        : "border-white/10 hover:border-white/20 bg-black/40"
                    }`}>
                    <div className="font-semibold text-white mb-1">{status.label}</div>
                    <div className="text-sm text-zinc-400">{status.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Price (only if PPV) */}
          {step === 3 && selections.status === "ppv" && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Set Price</h3>
                <p className="text-sm text-zinc-400 mb-4">What&apos;s the unlock price?</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white text-xl">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={selections.price}
                  onChange={(e) => setSelections({ ...selections, price: parseFloat(e.target.value) || 0 })}
                  className="flex-1 px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-2xl font-bold focus:outline-none focus:border-purple-500"
                  placeholder="0.00"
                />
              </div>
              <div className="text-sm text-zinc-500">
                Captions will reference the price in CTAs where appropriate
              </div>
            </div>
          )}

          {/* Step 4: Format Selection */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Caption Format</h3>
                <p className="text-sm text-zinc-400 mb-4">Choose length and style</p>
              </div>
              <div className="space-y-2">
                {FORMATS.map((format) => (
                  <button
                    key={format.id}
                    onClick={() => setSelections({ ...selections, format: format.id })}
                    className={`w-full p-4 rounded-lg border-2 text-left transition ${
                      selections.format === format.id
                        ? "border-purple-500 bg-purple-500/10"
                        : "border-white/10 hover:border-white/20 bg-black/40"
                    }`}>
                    <div className="font-semibold text-white mb-1">{format.label}</div>
                    <div className="text-sm text-zinc-400">{format.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Mood Selection */}
          {step === 5 && !generating && !result && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Select Mood</h3>
                <p className="text-sm text-zinc-400 mb-4">What voice should the captions have?</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {MOODS.map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => setSelections({ ...selections, mood: mood.id })}
                    className={`p-4 rounded-lg border-2 text-left transition ${
                      selections.mood === mood.id
                        ? "border-purple-500 bg-purple-500/10"
                        : "border-white/10 hover:border-white/20 bg-black/40"
                    }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{mood.emoji}</span>
                      <span className="font-semibold text-white">{mood.label}</span>
                    </div>
                    <div className="text-xs text-zinc-400">{mood.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Generating State */}
          {generating && (
            <div className="flex flex-col items-center gap-4 py-12">
              <RefreshCw size={32} className="animate-spin text-purple-500" />
              
              <div className="w-full max-w-md px-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white">Generating captions...</span>
                  <span className="text-zinc-400">{progress}%</span>
                </div>
                <div className="h-2 bg-zinc-800/50 rounded-full overflow-hidden backdrop-blur-xl">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              
              <p className="text-zinc-400 text-sm">
                This may take 30-60 seconds...
              </p>
            </div>
          )}

          {/* Result State */}
          {result && (
            <div className="space-y-4">
              <div className="text-center py-6">
                <div className="text-5xl mb-3">✅</div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {aiCopyFailed ? PROMO_AI_FAILURE_STATUS : "Promo Pack Generated!"}
                </h3>
                <p className="text-zinc-400 mb-2">
                  {selections.platforms.length} platform{selections.platforms.length !== 1 ? "s" : ""} · {selections.format === "all" ? "3 formats" : selections.format} · {MOODS.find(m => m.id === selections.mood)?.label} mood
                </p>
                <p className="text-sm font-semibold text-cyan-200">{tierCta}</p>
              </div>

              <label className="block text-xs text-zinc-400">Optional promo image text
                <input value={promoImageText} onChange={(e) => setPromoImageText(e.target.value)} placeholder="Short overlay text for a promo image"
                  className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500" />
              </label>

              {result?._batch_errors && Object.keys(result._batch_errors).length > 0 && (
                <div className="rounded-lg border border-amber-700/50 bg-amber-900/20 p-3 space-y-1 text-xs text-amber-200" data-testid="promo-batch-errors">
                  {Object.entries(result._batch_errors).map(([route, msg]) => (
                    <div key={route}><span className="font-semibold uppercase">{route} batch failed:</span> {msg}</div>
                  ))}
                </div>
              )}
              {result && (
                <div className="flex flex-wrap gap-2 text-xs" data-testid="promo-batch-summary">
                  {["safe", "explicit"].map((route) => {
                    const gens = Object.values(result._generation || {}).filter((g) => g.route === route);
                    if (gens.length === 0) return null;
                    const sets = [...new Set(gens.map((g) => `${g.provider} / ${g.model}`))].join(", ");
                    const fallback = gens.some((g) => g.fallback_used);
                    return (
                      <span key={route} className="px-2 py-1 rounded border border-white/10 text-zinc-300">
                        {route === "safe" ? "Safe" : "Explicit"} set: {sets}{fallback ? " (fallback used)" : ""}
                      </span>
                    );
                  })}
                </div>
              )}
              {result?._missing_platforms?.length > 0 && (
                <div className="text-xs text-amber-300" data-testid="promo-missing-platforms">
                  No copy returned for: {result._missing_platforms.map((p) => PLATFORMS.find((x) => x.id === p)?.label || p).join(", ")}. Use Regenerate on those sets.
                </div>
              )}

              {/* Caption Display with Copy Buttons */}
              <div className="space-y-4 max-h-[28rem] overflow-y-auto px-2" data-testid="promo-result-captions">
                {(() => {
                  const captions = result || {};
                  const rendered = Object.keys(captions)
                    .filter((key) => !key.startsWith("_") && normalizeCaptionEntries(captions[key]).length > 0)
                    .sort((a, b) => (PLATFORMS.findIndex((p) => p.id === a) + 1 || 99) - (PLATFORMS.findIndex((p) => p.id === b) + 1 || 99));
                  if (rendered.length === 0) {
                    return (
                      <div className="text-center text-zinc-400 py-8" data-testid="promo-no-captions">
                        No captions were returned for the selected platforms. Try regenerating.
                      </div>
                    );
                  }
                  return rendered.map((platform) => {
                    const platformData = captions[platform];
                    const platformInfo = PLATFORMS.find(p => p.id === platform);
                    const entries = normalizeCaptionEntries(platformData);
                    if (entries.length === 0) return null;
                    return (
                      <div key={platform} data-testid={`promo-platform-${platform}`} className="bg-gradient-to-br from-zinc-900/90 to-zinc-800/90 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-bold text-white flex items-center gap-2">
                            <span className="text-xl">{platformInfo?.emoji || "📝"}</span>
                            {platformInfo?.label || platform}
                            {captions._generation?.[platform] && (
                              <span className="text-[10px] font-normal text-zinc-400" data-testid={`promo-set-model-${platform}`}>
                                {captions._generation[platform].route === "safe" ? "Safe" : "Explicit"} · {captions._generation[platform].provider} / {captions._generation[platform].model}{captions._generation[platform].fallback_used ? " · fallback" : ""}
                              </span>
                            )}
                          </h4>
                          <button
                            onClick={() => {
                              const all = entries.map(e => `${e.label.toUpperCase()}\n${e.copyText}`).join("\n\n———\n\n");
                              navigator.clipboard.writeText(all);
                              toast.success(`All ${platformInfo?.label || platform} captions copied!`);
                            }}
                            className="text-xs px-3 py-1 border border-white/15 hover:border-purple-500 text-zinc-300 hover:text-white rounded transition flex items-center gap-1"
                            data-testid={`promo-copy-all-${platform}`}
                          >
                            <Copy size={12} /> Copy all
                          </button>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`promo-set-controls-${platform}`}>
                          <button
                            onClick={() => handleRegenerate(platform)}
                            disabled={!jobId || Boolean(regenerating[platform])}
                            className="text-xs px-3 py-1 border border-white/15 hover:border-purple-500 text-zinc-300 hover:text-white rounded transition disabled:opacity-50 flex items-center gap-1"
                            data-testid={`promo-regen-${platform}`}>
                            <RefreshCw size={12} className={regenerating[platform] ? "animate-spin" : ""} /> Regenerate
                          </button>
                          {editing?.platform === platform ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                disabled={savingEdit}
                                className="text-xs px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded transition disabled:opacity-50"
                                data-testid={`promo-save-${platform}`}>Save</button>
                              <button
                                onClick={() => setEditing(null)}
                                className="text-xs px-3 py-1 border border-white/15 text-zinc-300 hover:text-white rounded transition"
                                data-testid={`promo-cancel-${platform}`}>Cancel</button>
                            </>
                          ) : (
                            <button
                              onClick={() => startEdit(platform, captions[platform])}
                              disabled={!jobId}
                              className="text-xs px-3 py-1 border border-white/15 hover:border-purple-500 text-zinc-300 hover:text-white rounded transition disabled:opacity-50"
                              data-testid={`promo-edit-${platform}`}>Edit</button>
                          )}
                          {(captions._missing_platforms || []).includes(platform) && (
                            <span className="text-xs text-amber-300">No copy returned. Regenerate to retry.</span>
                          )}
                        </div>
                        <div className="space-y-3">
                          {entries.map((entry) => (
                            <div key={entry.label} className="bg-black/40 rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-purple-400 font-semibold uppercase">{entry.label}</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(entry.copyText);
                                    toast.success(`${entry.label} caption copied!`);
                                  }}
                                  className="text-xs px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded transition flex items-center gap-1"
                                  data-testid={`promo-copy-${platform}-${entry.label}`}
                                >
                                  <Copy size={12} /> Copy
                                </button>
                                  <button
                                    onClick={() => handleMarkAsUsed(platform, entry.copyText)}
                                    className="text-xs px-2 py-1 border border-cyan-700/60 text-cyan-200 hover:bg-cyan-900/30 rounded transition"
                                    data-testid={`promo-used-${platform}-${entry.label}`}
                                  >
                                    Mark as used
                                  </button>
                              </div>
                              {editing?.platform === platform ? (
                                <textarea
                                  rows={3}
                                  value={editing.drafts[entry.label] ?? ""}
                                  onChange={(e) => setEditing((s) => ({ ...s, drafts: { ...s.drafts, [entry.label]: e.target.value } }))}
                                  className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-purple-500"
                                  data-testid={`promo-edit-text-${platform}-${entry.label}`}
                                />
                              ) : (
                                <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid={`promo-text-${platform}-${entry.label}`}>{entry.text}</p>
                              )}
                              {entry.hashtags.length > 0 && (
                                <p className="text-xs text-purple-300/80 whitespace-pre-wrap">{entry.hashtags.join(" ")}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {!generating && !result && (
          <div className="sticky bottom-0 flex items-center justify-between p-6 border-t border-white/10 bg-black/95 backdrop-blur-xl">
            {step > 1 && (
              <button
                onClick={handleBack}
                className="px-6 py-2 border border-white/10 text-white rounded-lg hover:bg-zinc-800/50 transition">
                Back
              </button>
            )}
            <div className="flex-1" />
            {step < 5 ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition flex items-center gap-2">
                Next <ChevronRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={generating}
                className={`px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition flex items-center gap-2 ${generating ? 'animate-pulse' : ''}`}
              >
                {generating ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} /> Generate Promo Pack
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {result && (
          <div className="sticky bottom-0 p-6 border-t border-white/10 bg-black/95 backdrop-blur-xl">
            <button
              onClick={handleClose}
              className="w-full px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
